import { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ClipboardCopy } from 'lucide-react';
import clsx from 'clsx';
import { ToolHeader } from '../components/ToolHeader';
import {
  parseCurl,
  toAxios,
  toFetch,
  toRequests,
  toWget,
} from '../lib/curlConvert';

const SAMPLE_CURL = `curl -X POST 'https://api.example.com/v1/users' \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer abc.def.ghi' \\
  -u alice:secret \\
  -d '{"name":"foo","tag":42}'`;

type Lang = 'fetch' | 'axios' | 'requests' | 'wget';

const LANG_LABEL: Record<Lang, string> = {
  fetch: 'JS · fetch',
  axios: 'JS · axios',
  requests: 'Python · requests',
  wget: 'shell · wget',
};

/**
 * `/tools/network/curl-convert` — cURL ↔ four target snippets.
 *
 * Left: cURL textarea. Right: a tab strip with the four output
 * languages and a copy button. Parser warnings (file uploads,
 * unknown flags) appear above the output.
 *
 * Boundary with sibling `network/http`: that tool actually FIRES
 * the request from the browser; this one produces source code you
 * can paste into your editor / agent / runbook. Eventually the
 * pages will gain a "→ open in HTTP client" button (Wave 4 idea).
 */
export default function CurlConvertTool(): JSX.Element {
  const [input, setInput] = useState<string>(SAMPLE_CURL);
  const [lang, setLang] = useState<Lang>('fetch');
  const [copied, setCopied] = useState(false);

  const parsed = useMemo(() => parseCurl(input), [input]);

  const out = useMemo(() => {
    try {
      switch (lang) {
        case 'fetch':
          return toFetch(parsed);
        case 'axios':
          return toAxios(parsed);
        case 'requests':
          return toRequests(parsed);
        case 'wget':
          return toWget(parsed);
      }
    } catch (e) {
      return `// generation failed: ${e instanceof Error ? e.message : String(e)}`;
    }
  }, [parsed, lang]);

  const onCopy = async (): Promise<void> => {
    if (!out) return;
    try {
      await navigator.clipboard.writeText(out);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 900);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <ToolHeader
        toolId="curl-convert"
        category="network"
        description="把 cURL 命令翻译成 fetch / axios / Python requests / wget。支持 -X / -H / -d / -u / -F / -G / --url 等常见参数；不支持 @file 文件上传与 --proxy / --cacert（会给出警告）。"
      />

      <section className="card-pad flex flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            输入：cURL 命令
          </span>
          <span className="text-[11px] text-ink-dim">
            解析为 {parsed.method} {parsed.url || '<未指定 URL>'}
          </span>
        </header>
        <textarea
          className="input min-h-[140px] resize-y font-mono text-[13px]"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          spellCheck={false}
          placeholder="粘贴 cURL（支持 \\\n 跨行）"
        />
      </section>

      {parsed.warnings.length > 0 && (
        <ul className="card-pad border-amber-500/30 bg-amber-500/5 text-[11.5px] text-amber-700 dark:text-amber-300">
          {parsed.warnings.map((w, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              {w}
            </li>
          ))}
        </ul>
      )}

      <section className="card-pad flex min-h-0 flex-1 flex-col gap-2">
        <header className="flex items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {(Object.keys(LANG_LABEL) as Lang[]).map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setLang(l)}
                className={clsx(
                  'rounded border px-2 py-0.5 text-[12px] transition',
                  l === lang
                    ? 'border-accent bg-accent/10 text-accent'
                    : 'border-line text-ink-muted hover:border-accent/40 hover:text-ink',
                )}
              >
                {LANG_LABEL[l]}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={onCopy}
            disabled={!out}
            className="btn-ghost px-2 py-0.5"
            title="复制结果"
          >
            {copied ? (
              <>
                <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                已复制
              </>
            ) : (
              <>
                <ClipboardCopy className="h-3 w-3" />
                复制
              </>
            )}
          </button>
        </header>
        <textarea
          className="input flex-1 resize-none font-mono text-[13px]"
          value={out}
          readOnly
          spellCheck={false}
        />
      </section>
    </div>
  );
}
